import React, { createContext, useCallback, useState } from 'react';
import { AxiosInstance } from 'axios';

import { useLD } from '@/hooks/useLD';

import { HttpService, THttpServiceConfig } from '@/services/HttpService';

import SessionTokenHelper from '@/helpers/AuthenticationHelper/SessionTokenHelper';
import { getValue } from '@/helpers/GetValueHelper';
import { getUuid } from '@/helpers/StringHelper/uuid';

import { TApiProps, TParams } from './types';

export const ApiContext = createContext({} as TApiProps);

const ApiProvider = ({ children }: TParams): JSX.Element | null => {
  const { interactionsURL, productConfigURL } = useLD();

  const [traceId, setTraceId] = useState<string>('');

  function getApiRulesConfig(): THttpServiceConfig {
    return {
      baseURL: '',
      headers: {
        Accept: 'application/json',
      },
      credentials: 'omit',
    };
  }

  const getApiModalConfig = useCallback((): THttpServiceConfig => {
    return {
      baseURL: '',
      headers: {
        Accept: 'application/json',
      },
      credentials: 'omit',
    };
  }, []);

  const getProductConfig = useCallback((): THttpServiceConfig => {
    return {
      baseURL: productConfigURL,
      headers: {
        Accept: 'application/json',
      },
      credentials: 'omit',
    };
  }, [productConfigURL]);

  const getApiInteractionsConfig = useCallback((): THttpServiceConfig => {
    return {
      baseURL: interactionsURL,
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${SessionTokenHelper()}`,
      },
      credentials: 'omit',
    };
  }, [interactionsURL]);

  const ApiRules = HttpService(getApiRulesConfig());
  const ApiModal = HttpService(getApiModalConfig());
  const ApiProduct = HttpService(getProductConfig());
  const ApiInteractions = HttpService(getApiInteractionsConfig());

  /* istanbul ignore next */
  (ApiInteractions as AxiosInstance).interceptors.request.use(
    config => {
      if (traceId) {
        const method = getValue(config, 'method', '') as string;
        if (method.toLocaleUpperCase() === 'GET') {
          return {
            ...config,
            params: {
              ...config.params,
              trace_id: traceId,
              request_id: getUuid(),
            },
          };
        }

        if (method?.toLocaleUpperCase() === 'POST') {
          return {
            ...config,
            data: { ...config.data, trace_id: traceId, request_id: getUuid() },
          };
        }
      }

      return config;
    },
    error => {
      return Promise.reject(error);
    },
  );

  return (
    <ApiContext.Provider
      value={{
        ApiRules,
        ApiModal,
        ApiProduct,
        ApiInteractions,
        setTraceId,
      }}
    >
      {children}
    </ApiContext.Provider>
  );
};

export default ApiProvider;
